import ButtonSimple from "@/components/ButtonSimple";
import TooltipCustom from "@/components/TooltipCustom";
import { handleExport, lineBreakerForChartTitle } from "@/utils";
import { IconAiOutlineDownload } from "@/utils/icons";
import html2canvas from "html2canvas";
import React, { useCallback, useEffect, useRef } from "react";
import embed from "vega-embed";

const ChartColumnGrouped = ({ item }: any) => {

  const chartData = item?.data || [];
  const legendTitle = item?.legend_title || '';
  const uniqListOfX = [...new Set(chartData?.map((item: any) => item?.category || ''))];
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewRef = useRef<any>(null);
  const chartRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const customChartRef = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(() => {
    if (viewRef.current) {
      viewRef.current.toImageURL('png', 2).then((url: string) => {
        const link = document.createElement('a');
        link.download = `${item?.title || 'chart'}.png`;
        link.href = url;
        link.click();
      });
    }
  }, [item?.title]);


  const renderChart = useCallback(() => {

    if (!chartRef.current || !chartData) {
      return;
    }

    const chartWidth = chartRef.current.offsetWidth > 400 ? chartRef.current.offsetWidth : 400;


    const title = item?.title || "";
    const formattedTitle = lineBreakerForChartTitle(title, chartWidth);

    const spec: any = {
      "$schema": "https://vega.github.io/schema/vega/v5.json",
      "description": item?.description || "",
      "width": chartWidth,
      "height": 400,
      "padding": { "top": 0, "bottom": 0, "left": 0, "right": 10 },
      "autosize": { "type": "fit", "contains": "padding" },
      "data": [
        {
          "name": "table",
          values: chartData
        }
      ],
      "config": {
        "title": { "font": "Arial", "fontSize": 12 },
        "text": { "font": "Arial", "fontSize": 10 },
        "axis": { "labelFont": "Arial", "labelFontSize": "10", "labelColor": "gray" }
      },
      "title": {
        "text": formattedTitle,
        "fontSize": 12,
        "fontFamily": "Arial",
        "fontWeight": "bold",
        "color": "black",
        "anchor": "middle",
        "lineHeight": 14
      },
      "scales": [
        {
          "name": "xscale",
          "type": "band",
          "domain": { "data": "table", "field": "category" },
          "range": "width",
          "padding": 0.2
        },
        {
          "name": "yscale",
          "type": "linear",
          "domain": { "data": "table", "field": "value" },
          "range": "height",
          "round": true,
          "zero": true,
          "nice": true
        },
        {
          "name": "color",
          "type": "ordinal",
          "domain": { "data": "table", "field": "yearVal" },
          "range": { "data": "table", "field": "colorValue" }
        }
      ],

      "axes": [
        {
          "orient": "bottom",
          "scale": "xscale",
          "labelLimit": { "signal": "bandwidth('xscale')" },
          "labelFontSize": 9,
          "labelColor": "black",
          "labelAlign": "center",
          "labelBaseline": "top",
          "labelPadding": 4,
          "zindex": 1,
        },
        { "orient": "left", "scale": "yscale" }
      ],

      // "axes": [
      //   {
      //     "orient": "bottom",
      //     "scale": "xscale",
      //     "labelLimit": { "signal": "bandwidth('x')" },
      //     "labelFontSize": 9,
      //     "labelColor": "black",
      //     "labelMultiline": true,
      //     "labelAlign": "center",
      //     "labelBaseline": "top"
      //   },
      //   {
      //     "orient": "left",
      //     "scale": "yscale",
      //     "zindex": 1
      //   }
      // ],

      "marks": [
        {
          "type": "group",
          "from": {
            "facet": {
              "data": "table",
              "name": "facet",
              "groupby": "category"
            }
          },
          "encode": {
            "enter": {
              "x": { "scale": "xscale", "field": "category" }
            }
          },
          "signals": [
            { "name": "width", "update": "bandwidth('xscale')" }
          ],
          "scales": [
            {
              "name": "pos",
              "type": "band",
              "range": "width",
              "domain": { "data": "facet", "field": "position" }
            }
          ],
          "marks": [
            {
              "name": "bars",
              "from": { "data": "facet" },
              "type": "rect",
              "encode": {
                "enter": {
                  "x": { "scale": "pos", "field": "position" },
                  "width": { "scale": "pos", "band": 1 },
                  "y": { "scale": "yscale", "field": "value" },
                  "y2": { "scale": "yscale", "value": 0 },
                  "fill": { "scale": "color", "field": "yearVal" }
                }
              }
            },
            {
              "type": "text",
              "from": { "data": "bars" },
              "encode": {
                "enter": {
                  "x": { "field": "x", "offset": { "field": "width", "mult": 0.5 } },
                  "y": { "field": "y", "offset": -5 },
                  "fill": [
                    { "test": "datum.value > 0.5", "value": "black" },
                    { "value": "black" }
                  ],
                  "align": { "value": "center" },
                  "baseline": { "value": "bottom" },
                  "text": { "field": "datum.value" }
                }
              }
            }
          ],
        },
      ]
    }

    if (legendTitle) {
      Object.assign(spec, {
        "legends": [
          {
            "fill": "color",
            "title": legendTitle,
            "orient": "right",
            "titleFont": "Arial",
            "padding": 0,
            "titleFontSize": 10,
            "labelFont": "Arial",
            "labelFontSize": 10
          }
        ],
      })
    }


    if (chartRef.current) {
      chartRef.current.innerHTML = '';
    }

    embed(chartRef.current as unknown as HTMLElement, spec, {
      actions: {
        export: true,
        source: false,
        compiled: false,
        editor: false
      }
    }).then((stackedColumnBarView: any) => {
      viewRef.current = stackedColumnBarView.view;
      const menu = chartRef.current?.querySelector(".vega-actions");
      if (menu) {
        const svgButton = menu.querySelector('a[title="Save as SVG"]');
        if (svgButton) {
          svgButton.remove();
        }
      }
    });

  }, [item]);


  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      renderChart();
    }, 300);
  }, [renderChart]);


  useEffect(() => {

    renderChart();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [renderChart, handleResize]);


  const exportAsPNG = async () => {
    if (customChartRef.current) {
      const canvas = await html2canvas(customChartRef.current, { scale: 2 });
      const link = document.createElement('a');
      link.download = `${item?.title || 'table'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };


  return (
    <div>
      <TooltipCustom title="Export as PNG">
        <ButtonSimple
          // onClick={handleExport}
          onClick={exportAsPNG}
          className='flex justify-self-end mb-1'
        >
          <IconAiOutlineDownload />
        </ButtonSimple>
      </TooltipCustom>
      <div ref={customChartRef}>
        <div id="chart-grouped-column-bar" className="!p-0 !pr-0 !pe-0 " ref={chartRef}></div>
        <div className="pb-2 pl-1">
          {uniqListOfX.map((item: any, index: number) => (
            <div key={item} className="mt-1 pb-[2px] leading-[7px] text-[10px] text-[#000000] ">
              <b>{index + 1}.</b> {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChartColumnGrouped;
